import React, { useState, useEffect } from 'react';
import { Star, Loader, AlertCircle, TrendingUp } from 'lucide-react';
import API from '../../api/API';
import './AClassifiche.css';
import './AFantascoutball.css';

const AFantascoutball = () => {
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [formula, setFormula] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('selectedEdition');
    if (stored) {
      const edizione = JSON.parse(stored);
      setEdizioneSelezionata(edizione);
      fetchData(edizione.id);
    }
  }, []);

  const fetchData = async (edizioneId) => {
    setLoading(true);
    setError(null);
    try {
      const [rankRes, formulaRes] = await Promise.all([
        API.fanta.classifica(edizioneId),
        API.fanta.formula(),
      ]);

      if (rankRes.isSuccess) {
        setRanking(rankRes.data || []);
      } else {
        setError(rankRes.errorMsg || 'Errore nel caricamento della classifica');
      }

      if (formulaRes.isSuccess) {
        setFormula(formulaRes.data);
      }
    } catch (err) {
      setError(err.message || 'Errore nel caricamento della classifica');
    } finally {
      setLoading(false);
    }
  };

  const formulaExpression = () => {
    if (!formula) return '';
    const sign = (n) => (n >= 0 ? '+' : '−');
    const abs = (n) => Math.abs(n);
    const parts = [
      `mete × ${formula.mete}`,
      `${sign(formula.pieni)} pieni × ${abs(formula.pieni)}`,
      `${sign(formula.vuoti)} vuoti × ${abs(formula.vuoti)}`,
      `${sign(formula.assist)} assist × ${abs(formula.assist)}`,
      `${sign(formula.gialli)} gialli × ${abs(formula.gialli)}`,
      `${sign(formula.rossi)} rossi × ${abs(formula.rossi)}`,
    ];
    let expr = parts.join(' ');
    if (formula.dqSuNero) expr += ' (neri ⇒ DQ)';
    return expr;
  };

  const LoadingSpinner = () => (
    <div className="admin-loading">
      <Loader className="admin-loading-icon" />
      <h3 className="admin-loading-title">Caricamento classifica...</h3>
      <p className="admin-loading-text">Attendere prego</p>
    </div>
  );

  const ErrorMessage = () => (
    <div className="admin-error">
      <AlertCircle className="admin-error-icon" />
      <h3 className="admin-error-title">Errore nel caricamento</h3>
      <p className="admin-error-text">{error}</p>
      {edizioneSelezionata && (
        <button
          onClick={() => fetchData(edizioneSelezionata.id)}
          className="admin-retry-button"
        >
          Riprova
        </button>
      )}
    </div>
  );

  const FormulaLine = () => (
    <p className="fanta-formula-line">
      <strong>Formula punteggio:</strong> {formulaExpression()}
    </p>
  );

  const RankTable = () => (
    <div className="fanta-table-container">
      <table className="fanta-table">
        <thead>
          <tr>
            <th className="fanta-col-pos">#</th>
            <th className="fanta-col-num">N.</th>
            <th>Nome</th>
            <th>Cognome</th>
            <th>Maglia</th>
            <th>Squadra</th>
            <th className="fanta-col-stat">Sesso</th>
            <th className="fanta-col-stat">Mete</th>
            <th className="fanta-col-stat">Pieni</th>
            <th className="fanta-col-stat">Vuoti</th>
            <th className="fanta-col-stat">Assist</th>
            <th className="fanta-col-stat" title="Gialli">G</th>
            <th className="fanta-col-stat" title="Rossi">R</th>
            <th className="fanta-col-stat" title="Neri">N</th>
            <th className="fanta-col-rank">Punti</th>
          </tr>
        </thead>
        <tbody>
          {ranking.map((r, idx) => {
            const podiumClass = idx < 3 ? `fanta-row-podium-${idx + 1}` : '';
            const badgeClass = idx < 3 ? `fanta-pos-${idx + 1}` : '';
            const sesso = (r.sesso || '').toLowerCase();
            const genderClass = sesso === 'f' ? 'fanta-gender-f' : 'fanta-gender-m';
            return (
              <tr key={r.idP} className={podiumClass}>
                <td className="fanta-col-pos">
                  <span className={`fanta-pos-badge ${badgeClass}`}>{idx + 1}</span>
                </td>
                <td className="fanta-col-num">{r.numero}</td>
                <td>{r.nome}</td>
                <td>{r.cognome}</td>
                <td>{r.maglia}</td>
                <td>{r.squadraN}</td>
                <td className="fanta-col-stat">
                  <span className={`fanta-gender ${genderClass}`}>{sesso || '-'}</span>
                </td>
                <td className="fanta-col-stat">{r.mete}</td>
                <td className="fanta-col-stat">{r.pieni}</td>
                <td className="fanta-col-stat">{r.vuoti}</td>
                <td className="fanta-col-stat">{r.assist}</td>
                <td className="fanta-col-stat">{r.gialli}</td>
                <td className="fanta-col-stat">{r.rossi}</td>
                <td className="fanta-col-stat">{r.neri}</td>
                <td className="fanta-col-rank">{r.squalificato ? 'DQ' : r.ranking}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-header-content">
          <Star className="admin-header-icon" />
          <div>
            <h1 className="admin-title">Fantascoutball</h1>
            <p className="admin-subtitle">Classifica individuale dei giocatori secondo la formula fanta</p>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage />
      ) : !edizioneSelezionata ? (
        <div className="admin-no-selection">
          <TrendingUp className="no-selection-icon" />
          <h3>Nessuna edizione selezionata</h3>
          <p>Seleziona un'edizione dalla pagina Tornei per visualizzare la fanta-classifica</p>
        </div>
      ) : (
        <>
          <FormulaLine />
          {ranking.length === 0 ? (
            <div className="admin-no-selection">
              <TrendingUp className="no-selection-icon" />
              <h3>Nessun dato disponibile</h3>
              <p>Nessun giocatore iscritto o nessuna statistica registrata</p>
            </div>
          ) : (
            <RankTable />
          )}
        </>
      )}
    </div>
  );
};

export default AFantascoutball;
